import { useTranslation } from '@exam-room/translations';
import { useWindowEvent } from '@shared/hooks';
import { useRef } from 'react';
import { generatePath, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ArrowBackRoundedIcon,
  ArrowForwardRoundedIcon,
  Button,
  FactCheckOutlinedIcon,
  Footer,
} from '../../../components';
import { useNewExam } from '../../../providers';
import { PATHS } from '../../../routes/constants';

export default function ExamFooter() {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { examCardId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({});
  const q = searchParams.get('q');
  const page = searchParams.get('page') ?? '';

  const { questions } = useNewExam();

  const qIndex = questions?.findIndex((question) => question.id === q);
  const qIndexValid = typeof qIndex === 'number'; // Be careful about 0 is false.

  const isReviewPage = page === 'review';

  function backOneQuestion() {
    if (!questions || q === questions[0].id || !qIndexValid) return;

    const params = new URLSearchParams();
    if (isReviewPage) {
      params.set('q', questions[questions.length - 1].id);
    } else {
      params.set('q', questions[qIndex - 1].id);
    }
    params.set('page', 'exam');
    const query = params.toString();
    setSearchParams(query);
  }

  function forwardOneQuestion() {
    // Allow submit if the exam is MOCK or PRACTICE (with timeLimit)
    if (isReviewPage) {
      navigate(generatePath(PATHS.Exam.Processing, { examCardId }));
      return;
    }

    if (isReviewPage || !questions || !qIndexValid) return;

    const params = new URLSearchParams();
    if (qIndex + 1 === questions.length) {
      params.set('q', questions[qIndex].id);
      params.set('page', 'review');
    } else {
      params.set('q', questions[qIndex + 1].id);
      params.set('page', 'exam');
    }

    const query = params.toString();
    setSearchParams(query);
  }

  const footerRef = useRef<HTMLDivElement | null>(null);

  useWindowEvent('keydown', (e) => {
    if (footerRef.current) {
      if (e.key === 'ArrowLeft') {
        backOneQuestion();
      } else if (e.key === 'ArrowRight') {
        // Disallow keyboard navigation to submit exam module
        if (page === 'review') return;

        forwardOneQuestion();
      }
    }
  });

  const navigateToReviewPage = () => {
    const params = new URLSearchParams();
    params.set('q', questions[qIndex].id);
    params.set('page', 'review');
    const query = params.toString();
    setSearchParams(query);
  };

  if (isReviewPage) return null;

  const isPreviousButtonDisabled = qIndex <= 0;
  const isNextButtonDisabled = isReviewPage || qIndex === (questions?.length ?? 0) - 1;

  return (
    <Footer
      ref={footerRef}
      middle={
        <div className='flex items-center gap-4'>
          <Button
            disabled={isPreviousButtonDisabled}
            type='submit'
            variant='outlined'
            className='w-36'
            onClick={backOneQuestion}
          >
            <div className='flex items-center justify-center gap-2'>
              <ArrowBackRoundedIcon className='hidden size-6 shrink-0 stroke-2 md:block' />{' '}
              {t('footer.previous')}
            </div>
          </Button>
          <Button
            type='submit'
            variant='outlined'
            onClick={forwardOneQuestion}
            disabled={isNextButtonDisabled}
            className='w-36'
          >
            <div className='flex items-center justify-center gap-2'>
              {t('footer.next')}
              <ArrowForwardRoundedIcon className='hidden size-6 shrink-0 stroke-2 md:block' />
            </div>
          </Button>
        </div>
      }
      right={
        !isReviewPage ? (
          <Button className='min-w-[196px]' onClick={navigateToReviewPage}>
            <div className='flex items-center justify-center gap-2'>
              <FactCheckOutlinedIcon className='shrink-0' /> {t('footer.review')}
            </div>
          </Button>
        ) : null
      }
    />
  );
}
